import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import {
  getWorkspaceJoinCode,
  rotateJoinCode,
  listTeamMembers,
  listPendingInvites,
  createInvite,
  revokeInvite,
  sendInviteEmail,
  getInviteAcceptLink,
  parseInviteError,
  type InviteRole,
  type TeamMember,
  type WorkspaceInvitation,
} from '@/lib/teamInvites';
import { Settings as SettingsIcon, Users, Link2, Building2, CreditCard, Mail, Shield, Crown, UserCog, Copy, Check, Trash2, Loader2, Plus, RotateCw, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/ui/page-header';
import { SectionCard } from '@/components/ui/section-card';
import { ListSkeleton } from '@/components/ui/skeletons';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Alert, AlertDescription } from '@/components/ui/alert';

/** Returns a valid Date or null if value is missing/invalid. */
function safeDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format join date for display; returns "—" if null/invalid. */
function formatJoinedAt(value: string | null | undefined): string {
  const d = safeDate(value);
  return d ? d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

/** Format date with date-fns; returns "—" if null/invalid. */
function formatSafeDate(value: unknown, formatStr: string): string {
  const d = safeDate(value);
  return d ? format(d, formatStr) : '—';
}

const settingsTabs = [
  { id: 'workspace', label: 'Workspace', icon: Building2, path: '/app/settings' },
  { id: 'team', label: 'Team', icon: Users, path: '/app/settings/team' },
  { id: 'integrations', label: 'Integrations', icon: Link2, path: '/app/settings/integrations' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/app/settings/billing' },
];

export default function Team() {
  const { workspace, profile } = useWorkspace();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('member');
  const [codeCopied, setCodeCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** When set, invite was created but email failed; show "Copy invite link" in modal */
  const [lastInviteTokenForCopy, setLastInviteTokenForCopy] = useState<string | null>(null);

  // Fetch join code
  const { data: joinCode, isLoading: joinCodeLoading, refetch: refetchJoinCode } = useQuery({
    queryKey: ['workspace-join-code', activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      return getWorkspaceJoinCode(activeWorkspaceId);
    },
    enabled: !!activeWorkspaceId,
  });

  // Fetch team members
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['team-members', activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      return listTeamMembers(activeWorkspaceId);
    },
    enabled: !!activeWorkspaceId,
  });

  // Fetch pending invites
  const { data: pendingInvites, isLoading: invitesLoading, refetch: refetchInvites } = useQuery({
    queryKey: ['pending-invites', activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      return listPendingInvites(activeWorkspaceId);
    },
    enabled: !!activeWorkspaceId,
  });

  // Rotate join code mutation
  const rotateCodeMutation = useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      return rotateJoinCode(activeWorkspaceId);
    },
    onSuccess: (newCode) => {
      queryClient.setQueryData(['workspace-join-code', activeWorkspaceId], newCode);
      toast({ title: 'Join code rotated', description: 'A new join code has been generated.' });
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: parseInviteError(error), 
        variant: 'destructive' 
      });
    },
  });

  // Create and send invite mutation (invite is created first; if email fails, we still have invite + copy link)
  const createInviteMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: InviteRole }) => {
      if (!activeWorkspaceId || !workspace || !profile) {
        throw new Error('Missing workspace or profile');
      }

      const result = await createInvite(activeWorkspaceId, email, role);

      try {
        await sendInviteEmail({
          workspaceId: activeWorkspaceId,
          workspaceName: workspace.name ?? 'Decision Sales OS',
          inviterName: profile.name || user?.email?.split('@')[0] || 'Someone',
          email,
          token: result.token,
          role,
        });
      } catch {
        return { result, emailFailed: true as const };
      }
      return { result, emailFailed: false as const };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites', activeWorkspaceId] });
      if (data.emailFailed) {
        toast({
          title: 'Invite created, email failed',
          description: 'You can copy the invite link and share it manually.',
          variant: 'destructive',
        });
        setLastInviteTokenForCopy(data.result.token);
        setError(null);
      } else {
        toast({ title: 'Invite sent', description: 'The invitation email has been sent.' });
        setInviteEmail('');
        setInviteModalOpen(false);
        setLastInviteTokenForCopy(null);
        setError(null);
      }
    },
    onError: (error) => {
      const errorMessage = parseInviteError(error);
      setError(errorMessage);
      if (errorMessage.includes('Seat limit')) {
        toast({
          title: 'Seat limit reached',
          description: errorMessage,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    },
  });

  // Revoke invite mutation
  const revokeInviteMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      return revokeInvite(invitationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites', activeWorkspaceId] });
      toast({ title: 'Invite revoked', description: 'The invitation has been cancelled.' });
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: parseInviteError(error), 
        variant: 'destructive' 
      });
    },
  });

  // Resend invite mutation (new token; if email fails, invite still updated)
  const resendInviteMutation = useMutation({
    mutationFn: async (invite: WorkspaceInvitation) => {
      if (!workspace || !profile) {
        throw new Error('Missing workspace or profile');
      }

      const result = await createInvite(activeWorkspaceId!, invite.email, invite.role);

      try {
        await sendInviteEmail({
          workspaceId: activeWorkspaceId!,
          workspaceName: workspace.name ?? 'Decision Sales OS',
          inviterName: profile.name || user?.email?.split('@')[0] || 'Someone',
          email: invite.email,
          token: result.token,
          role: invite.role,
        });
      } catch {
        return { result, emailFailed: true as const };
      }
      return { result, emailFailed: false as const };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites', activeWorkspaceId] });
      if (data.emailFailed) {
        toast({
          title: 'Invite updated, email failed',
          description: 'A new invite was created but the email could not be sent. Copy the invite link from the invite form if needed.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Invite resent', description: 'A new invitation email has been sent.' });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: parseInviteError(error),
        variant: 'destructive',
      });
    },
  });

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-3 h-3" />;
      case 'admin':
        return <Shield className="w-3 h-3" />;
      case 'member':
        return <UserCog className="w-3 h-3" />;
      case 'viewer':
        return <Mail className="w-3 h-3" />;
      default:
        return <Shield className="w-3 h-3" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
      case 'admin':
        return 'default';
      case 'member':
        return 'secondary';
      case 'viewer':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const handleCopyInviteCode = () => {
    if (!joinCode) return;
    navigator.clipboard.writeText(joinCode);
    setCodeCopied(true);
    toast({ title: 'Invite code copied!' });
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setError(null);
    setLastInviteTokenForCopy(null);
    createInviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const handleCopyInviteLink = () => {
    if (!lastInviteTokenForCopy) return;
    const link = getInviteAcceptLink(lastInviteTokenForCopy);
    navigator.clipboard.writeText(link);
    setCodeCopied(true);
    toast({ title: 'Invite link copied!' });
    setTimeout(() => setCodeCopied(false), 2000);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title="Settings"
        description="Manage your workspace and integrations"
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-border/40">
        {settingsTabs.map((tab) => (
          <Link
            key={tab.id}
            to={tab.path}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab.path === '/app/settings/team'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      {/* Team Content */}
      <div className="max-w-2xl space-y-6">
        {/* Invite Code Card */}
        <SectionCard
          title="Workspace Invite Code"
          description="Share this code to let new members join your workspace."
          header={
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold mb-1">Workspace Invite Code</h3>
                <p className="text-xs text-muted-foreground">
                  Share this code to let new members join your workspace.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => rotateCodeMutation.mutate()}
                disabled={rotateCodeMutation.isPending || joinCodeLoading}
                title="Rotate join code"
              >
                {rotateCodeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          }
        >
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-sm">
              {joinCodeLoading ? '...' : joinCode ?? 'Generate code'}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyInviteCode}
              disabled={!joinCode}
              aria-label="Copy invite code"
            >
              {codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </SectionCard>

        {/* Members Card */}
        <SectionCard
          title="Team Members"
          description="Manage who has access to this workspace."
          header={
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold mb-1">Team Members</h2>
                <p className="text-sm text-muted-foreground">
                  Manage who has access to this workspace.
                </p>
              </div>
              <Button onClick={() => setInviteModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Invite
              </Button>
            </div>
          }
        >
          {membersLoading ? (
            <ListSkeleton items={3} />
          ) : members && members.length > 0 ? (
            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 p-3 rounded-lg border bg-background"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                      {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {(member as { full_name?: string | null }).full_name || member.name || member.email || '—'}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {member.email ?? '—'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Joined {formatJoinedAt(member.joined_at ?? (member as { created_at?: string | null }).created_at)}
                    </p>
                  </div>
                  <Badge variant={getRoleBadgeVariant(member.role)} className="capitalize gap-1">
                    {getRoleIcon(member.role)}
                    {member.role}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No team members found.</p>
            </div>
          )}
        </SectionCard>

        {/* Pending Invites */}
        {pendingInvites && pendingInvites.length > 0 && (
          <SectionCard
            title="Pending Invitations"
            description={`${pendingInvites.length} invitation${pendingInvites.length !== 1 ? 's' : ''} awaiting response`}
          >
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{invite.email}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="capitalize">{invite.role}</span> · 
                        Created {formatSafeDate(invite.created_at, 'MMM d')} · 
                        Expires {formatSafeDate(invite.expires_at, 'MMM d')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInviteMutation.mutate(invite)}
                      disabled={resendInviteMutation.isPending}
                      title="Resend invitation"
                    >
                      {resendInviteMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => revokeInviteMutation.mutate(invite.id)}
                      disabled={revokeInviteMutation.isPending}
                      title="Revoke invitation"
                    >
                      {revokeInviteMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Invite Modal */}
      <Dialog
        open={inviteModalOpen}
        onOpenChange={(open) => {
          setInviteModalOpen(open);
          if (!open) {
            setError(null);
            setLastInviteTokenForCopy(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your workspace.
            </DialogDescription>
          </DialogHeader>

          {lastInviteTokenForCopy ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  Invite was created but the email could not be sent. Copy the link below and share it manually.
                </AlertDescription>
              </Alert>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyInviteLink}
                  aria-label="Copy invite link"
                >
                  {codeCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {codeCopied ? ' Copied!' : ' Copy invite link'}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    setInviteModalOpen(false);
                    setInviteEmail('');
                    setLastInviteTokenForCopy(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSendInvite} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{String(error ?? 'Unknown error')}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setError(null);
                  }}
                  placeholder="colleague@company.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={(v: InviteRole) => setInviteRole(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {inviteRole === 'admin' && 'Full access to all features and settings.'}
                  {inviteRole === 'member' && 'Can manage leads and deals.'}
                  {inviteRole === 'viewer' && 'Read-only access to workspace data.'}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setInviteModalOpen(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createInviteMutation.isPending || !inviteEmail.trim()}
                >
                  {createInviteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Send Invite
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
